import { useState } from "react";
import { Card, Gate, Head, ScrollX, State } from "@authoxi/ui-shared";
import { AgentSigner } from "@authoxi/js/agentsig";
import { apiBase } from "@authoxi/ui-shared";
import { client } from "../api";

/**
 * Free observability — and the one page whose credential is a key you paste.
 *
 * Reading your own trail needs an **RFC 9421-signed request**: `X-Agent-Did` plus a signature
 * whose keyid matches the did's thumbprint. There is no session, no token and no cookie,
 * because possession of the key IS the account — no password to reset, no session to steal,
 * and no support path where we hand someone else's trail to a convincing email.
 *
 * So the page takes a seed, signs **in this browser**, and never transmits it. That is the
 * same shape the console uses for `sk_live_`, and it is the only way a page can demonstrate
 * the claim rather than describing it. The alternative — an explainer with a screenshot —
 * would undercut the whole pitch on the page that makes it.
 *
 * The seed is hex, because `key_to_seed()` returns hex. Four hand-rolled decoders once existed
 * here, three of them accepting base64url only, so the seed the product told people to store
 * was refused by the functions they actually called.
 */
export function Observe() {
  const [seed, setSeed] = useState("");
  const [state, setState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "error"; message: string } | { kind: "ok"; body: Trail }
  >({ kind: "idle" });

  async function read() {
    setState({ kind: "busy" });
    try {
      const signer = await AgentSigner.create(seed.trim());
      const path = "/observe/records?limit=50";
      const url = apiBase() + path;
      const headers = await signer.signRequest("GET", url);
      const resp = await fetch(url, {
        headers: { ...headers, "X-Agent-Did": signer.did, accept: "application/json" },
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        throw new Error(body?.error?.message ?? `HTTP ${resp.status}`);
      }
      setState({ kind: "ok", body: (await resp.json()) as Trail });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : "Could not read that trail.",
      });
    }
  }

  return (
    <>
      <Head
        pill="Free observability"
        title="Ship a signed audit trail with no account."
        lede="Your agent signs what it did, you POST it, and you read it back with the same key. The key IS the account — nobody else can read your trail, and there is nothing of yours for us to leak."
      />

      <div className="cards-2" style={{ display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: "var(--s6)", alignItems: "start" }}>
        <Card pad>
          <p className="label">Three lines</p>
          <p className="sm" style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)" }}>
            No signup, no key of ours, no network call to generate an identity. If you never pay
            us, this keeps working — a free tier that quietly needs a service is a trial.
          </p>
        </Card>
        <pre className="code">{`from authoxi.agentsig import AgentSigner, McpAuditor

signer  = AgentSigner.generate()
auditor = McpAuditor(signer, session_id="ses_1")

auditor.record(tool="search", args={"q": "invoices"})

# POST them whenever you like — the batch is idempotent,
# so a client that times out and retries does not double
# its own audit trail.
requests.post("https://api.authoxi.com/observe/records",
              json={"records": auditor.records})`}</pre>
      </div>

      <Card pad="lg" className="">
        <p className="label">Your trail, read back with the same key</p>
        <p className="sm" style={{ margin: "var(--s3) 0 var(--s4)", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
          Paste the seed <span className="mono">key_to_seed()</span> gave you. It is used to sign
          this one request <strong>in your browser</strong> and is never sent anywhere — the
          signature is what travels, and it is what proves the did without revealing the key.
        </p>
        <form
          className="tool-input"
          data-testid="observe-form"
          onSubmit={(e) => {
            e.preventDefault();
            void read();
          }}
        >
          <input
            className="input input-mono"
            type="password"
            data-testid="observe-seed"
            placeholder="your agent's seed (hex)"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
          />
          <button className="btn btn-go" type="submit" disabled={!seed || state.kind === "busy"}>
            {state.kind === "busy" ? "…" : "Read my trail"}
          </button>
        </form>

        {state.kind === "error" && (
          <State kind="error" title="Could not read that trail" detail={state.message} />
        )}
        {state.kind === "ok" && <Records body={state.body} />}
      </Card>

      <Gate
        client={client}
        source="observe"
        title="When you outgrow self-attested"
        body="These records say witness: self — you signed your own log. When you need somebody else to countersign, that is the paid product."
        cta="Tell me when"
      />
    </>
  );
}

interface Trail {
  signer_did: string;
  count: number;
  total: number;
  retention_limit: number;
  retention_note: string;
  records: {
    record_id: string;
    ts: string | null;
    tool: string;
    session_id: string;
    seq: number;
    caller: string | null;
    ok: boolean;
  }[];
}

function Records({ body }: { body: Trail }) {
  if (body.records.length === 0) {
    return (
      <State
        kind="empty"
        title="That key has shipped nothing yet."
        detail="The key is valid — there are simply no records under it. POST some and read them back."
      />
    );
  }
  return (
    <>
      <ScrollX>
        <table className="table" style={{ marginTop: "var(--s3)" }} data-testid="observe-records">
          <thead>
            <tr>
              <th>Time</th>
              <th>Tool</th>
              <th>Session</th>
              <th>Seq</th>
              <th>Caller</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {body.records.map((r) => (
              <tr key={r.record_id}>
                <td className="mono muted">{r.ts?.slice(11, 19) ?? "—"}</td>
                <td className="mono">{r.tool}</td>
                <td className="mono muted">{r.session_id}</td>
                <td className="mono muted">{r.seq}</td>
                <td className="mono muted">{r.caller ?? "(unidentified)"}</td>
                <td>
                  <span className={`v dim v-${r.ok ? "allow" : "deny"}`}>{r.ok ? "ok" : "failed"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
      <p className="sm muted" style={{ marginTop: "var(--s3)" }}>
        {body.total} record{body.total === 1 ? "" : "s"} under {body.signer_did.slice(0, 24)}…
        {" "}{body.retention_note}
      </p>
    </>
  );
}
